import React, { useState, useMemo, useRef, useEffect } from 'react';
import T from '../styles/theme';
import Icon from '../components/Icon';
import { Checkbox } from '../components/UI';
import { useStore } from '../data/store';
import { useAuth } from '../data/auth';
import { useToast } from '../components/Toast';
import TaskModal from '../components/TaskModal';

export default function TasksPage({ isMobile }) {
  const store = useStore();
  const auth = useAuth();
  const toast = useToast();
  const { tasks, toggleTask, addTask, contacts: CONTACTS } = store;
  const [filter, setFilter] = useState("Active");
  const [userFilter, setUserFilter] = useState("");
  const [search, setSearch] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [editId, setEditId] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [showUserPicker, setShowUserPicker] = useState(false);
  const [userSearch, setUserSearch] = useState("");
  const userPickerRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  useEffect(() => {
    const h = (e) => { if (userPickerRef.current && !userPickerRef.current.contains(e.target)) setShowUserPicker(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const today = new Date().toISOString().slice(0, 10);

  const filtered = useMemo(() => {
    let l = tasks.slice();
    if (filter === "Active") l = l.filter(t => !t.done);
    if (filter === "Completed") l = l.filter(t => t.done);
    if (filter === "High Priority") l = l.filter(t => t.pri === "high" && !t.done);
    if (filter === "Overdue") l = l.filter(t => !t.done && t.due < today);
    if (filter === "My Tasks") l = l.filter(t => !t.done && (t.assignee === auth.currentUser?._dbId || !t.assignee));
    if (userFilter) l = l.filter(t => t.assignee === userFilter);
    if (search) {
      const q = search.toLowerCase();
      l = l.filter(t => {
        const assigneeLabel = store.teamMembers.find(m => m._dbId === t.assignee)?.name || "";
        return (t.title + " " + assigneeLabel).toLowerCase().includes(q);
      });
    }
    const pri = { high: 0, medium: 1, low: 2 };
    return l.sort((a, b) => {
      if (a.done !== b.done) return a.done ? 1 : -1;
      return pri[a.pri] - pri[b.pri];
    });
  }, [tasks, filter, userFilter, search, today, store.currentUser]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paginated = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, currentPage, pageSize]);

  const done = tasks.filter(t => t.done).length;
  const total = tasks.length;
  const overdue = tasks.filter(t => !t.done && t.due < today).length;
  const highPri = tasks.filter(t => t.pri === "high" && !t.done).length;

  const toggleAll = () => { if (selected.size === filtered.length) setSelected(new Set()); else setSelected(new Set(filtered.map(t => t.id))); };
  const toggleOne = (id) => { const s = new Set(selected); s.has(id) ? s.delete(id) : s.add(id); setSelected(s); };

  const handleEdit = (task) => {
    setEditId(task.id);
    setShowAdd(false);
  };

  const handleAddClick = () => {
    if (!auth.hasPermission("tasks_create")) {
      toast?.error?.("You don't have permission to create tasks");
      return;
    }
    setShowAdd(true);
    setEditId(null);
  };

  const canDeleteTask = auth.hasPermission("tasks_delete");
  const canEditAny = auth.hasPermission("tasks_edit");
  const assigneeName = (assignedTo) =>
    store.teamMembers.find(m => m._dbId === assignedTo)?.name || "Unassigned";

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", background: "transparent", fontFamily: "inherit" }}>
      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>

      {/* ═══ VIEW PICKER ROW ═══ */}
      <div style={{ padding: "14px 24px 0", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div ref={userPickerRef} style={{ position: "relative" }}>
            <button onClick={() => { setShowUserPicker(!showUserPicker); setUserSearch(""); }} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 12px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: showUserPicker ? "rgba(255,255,255,0.08)" : "transparent", cursor: "pointer", fontFamily: "inherit", transition: "all .15s ease" }} onMouseEnter={e => { if (!showUserPicker) e.currentTarget.style.background = "rgba(255,255,255,0.04)"; }} onMouseLeave={e => { if (!showUserPicker) e.currentTarget.style.background = "transparent"; }}>
              <Icon name="users" size={14} color="#FAFAFA" />
              <span style={{ fontSize: 15, color: "#FAFAFA" }}>{userFilter ? (store.teamMembers.find(m => m._dbId === userFilter)?.name || "Unknown user") : "All Users"}</span>
              <span style={{ fontSize: 10, color: "#FAFAFA", opacity: 0.5, marginLeft: 2 }}>▾</span>
            </button>
            {showUserPicker && (
              <div style={{ position: "absolute", top: "calc(100% + 6px)", left: 0, width: 240, background: "#33363D", border: "1px solid rgba(217,219,221,0.12)", borderRadius: 8, boxShadow: "0 24px 64px rgba(0,0,0,0.80)", zIndex: 99999, overflow: "hidden" }}>
                <div style={{ padding: "12px 14px 10px", borderBottom: "1px solid rgba(217,219,221,0.08)" }}>
                  <input autoFocus value={userSearch} onChange={e => setUserSearch(e.target.value)} placeholder="Search users..." style={{ width: "100%", padding: "9px 12px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.10)", background: "rgba(255,255,255,0.04)", color: "#FAFAFA", fontSize: 13, fontFamily: "inherit", outline: "none", boxSizing: "border-box" }} />
                </div>
                <div style={{ maxHeight: 280, overflowY: "auto", padding: "8px 10px" }}>
                  <div onClick={() => { setUserFilter(""); setShowUserPicker(false); }} style={{ padding: "11px 14px", borderRadius: 8, cursor: "pointer", background: !userFilter ? "#4059AD" : "rgba(255,255,255,0.04)", color: !userFilter ? "#fff" : "#FAFAFA", fontSize: 13, marginBottom: 4 }} onMouseEnter={e => { if (userFilter) e.currentTarget.style.background = "rgba(255,255,255,0.08)"; }} onMouseLeave={e => { if (userFilter) e.currentTarget.style.background = "rgba(255,255,255,0.04)"; }}>
                    All Users
                  </div>
                  {(store.teamMembers || []).filter(m => !userSearch || m.name.toLowerCase().includes(userSearch.toLowerCase())).map(m => (
                    <div key={m._dbId || m.id || m.name} onClick={() => { setUserFilter(m._dbId); setShowUserPicker(false); }} style={{ padding: "11px 14px", borderRadius: 8, cursor: "pointer", background: userFilter === m._dbId ? "#4059AD" : "rgba(255,255,255,0.04)", color: userFilter === m._dbId ? "#fff" : "#FAFAFA", fontSize: 13, marginBottom: 4 }} onMouseEnter={e => { if (userFilter !== m._dbId) e.currentTarget.style.background = "rgba(255,255,255,0.08)"; }} onMouseLeave={e => { if (userFilter !== m._dbId) e.currentTarget.style.background = "rgba(255,255,255,0.04)"; }}>
                      {m.name}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <span style={{ fontSize: 12, color: "#FAFAFA", opacity: 0.5 }}>{filtered.length} tasks</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 10, color: T.tx3 }}>{done}/{total} done</span>
          {overdue > 0 && <span style={{ fontSize: 10, color: "#E53935" }}>{overdue} overdue</span>}
          {highPri > 0 && <span style={{ fontSize: 10, color: T.tx3 }}>{highPri} high</span>}
        </div>
      </div>

      {/* ═══ SEARCH + FILTERS ROW ═══ */}
      <div style={{ padding: "10px 24px", display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(217,219,221,0.12)", borderRadius: 8, padding: "5px 10px", width: 220 }}>
          <Icon name="search" size={12} color={T.tx3} />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search..."
            style={{ border: "none", outline: "none", background: "transparent", color: "#FAFAFA", fontSize: 12, width: "100%", fontFamily: "inherit" }} />
        </div>
        <div style={{ display: "flex", gap: 2 }}>
          {["Active", "My Tasks", "High Priority", "Overdue", "All", "Completed"].map(f => (
            <button key={f} onClick={() => setFilter(f)} style={{
              padding: "5px 10px", borderRadius: 6, border: "none", cursor: "pointer", fontFamily: "inherit",
              background: filter === f ? "rgba(64,89,173,0.15)" : "transparent",
              color: filter === f ? "#FAFAFA" : T.tx3, fontSize: 10,
            }}>{f}</button>
          ))}
        </div>
        <div style={{ flex: 1 }} />
        <button onClick={handleAddClick} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 14px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: "transparent", color: "#FAFAFA", fontSize: 11, cursor: "pointer", fontFamily: "inherit" }}>+ Add Task</button>
      </div>

      {/* ═══ TABLE ═══ */}
      <div style={{ flex: 1, overflowY: "auto", overflowX: "auto", padding: "0 24px" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ borderBottom: "1px solid " + T.bdr, position: "sticky", top: 0, background: "#2C2E33", zIndex: 2 }}>
              <th style={{ padding: "10px 12px", width: 36 }} />
              {["TASK", "CONTACT", "ASSIGNED TO", "PRIORITY", "DUE DATE", "STATUS"].map(h => (
                <th key={h} style={{ padding: "10px 12px", textAlign: h === "TASK" ? "left" : "center", fontSize: 10, color: T.tx3, letterSpacing: ".06em", textTransform: "uppercase", whiteSpace: "nowrap", userSelect: "none" }}>{h}</th>
              ))}
              <th style={{ width: 40 }} />
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={8} style={{ padding: 0 }}><div style={{ padding: 40, textAlign: "center", color: T.tx3, fontSize: 12, position: "sticky", left: 0, width: "calc(100vw - 280px)" }}>No tasks match this filter</div></td></tr>
            ) : (
              paginated.map(t => {
                const ct = CONTACTS.find(c => c.id === t.ct);
                const isOverdue = !t.done && t.due < today;
                const canEditThis = canEditAny || (t.assignee && t.assignee === auth.currentUser?._dbId);

                return (
                  <tr key={t.id} style={{ borderBottom: "1px solid " + T.bdr, opacity: t.done ? 0.4 : 1, transition: "background 0.1s" }}
                    onMouseEnter={e => e.currentTarget.style.background = T.bg}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                    <td style={{ padding: "10px 12px" }} onClick={e => e.stopPropagation()}>
                      <Checkbox checked={t.done} onChange={() => toggleTask(t.id)} />
                    </td>
                    <td
                      style={{ padding: "10px 12px", textAlign: "left", cursor: canEditThis ? "pointer" : "default" }}
                      onClick={() => { if (!canEditThis) return; handleEdit(t); }}
                    >
                        <span style={{ fontSize: 12, color: "#FAFAFA", textDecoration: t.done ? "line-through" : "none" }}>{t.title}</span>
                    </td>
                    <td style={{ padding: "10px 12px", textAlign: "center", fontSize: 11, color: T.tx3 }}>
                      {ct ? ct.fn + " " + ct.ln : "—"}
                    </td>
                    <td style={{ padding: "10px 12px", textAlign: "center", fontSize: 11, color: T.tx3 }}>
                      {assigneeName(t.assignee)}
                    </td>
                    <td style={{ padding: "10px 12px", textAlign: "center", fontSize: 11, color: "#FAFAFA", textTransform: "capitalize" }}>
                      {t.pri}
                    </td>
                    <td style={{ padding: "10px 12px", textAlign: "center", fontSize: 11, color: T.tx3 }}>
                      {t.due || "—"}
                    </td>
                    <td style={{ padding: "10px 12px", textAlign: "center", fontSize: 11 }}>
                      {t.done ? <span style={{ color: T.tx3 }}>Done</span>
                        : isOverdue ? <span style={{ color: "#E53935" }}>OVERDUE</span>
                        : <span style={{ color: T.tx3 }}>Active</span>}
                    </td>
                    <td style={{ padding: "10px 12px", textAlign: "center" }}>
                      <button
                        onClick={() => { if (!canDeleteTask) return; store.deleteTask(t.id); }}
                        disabled={!canDeleteTask}
                        style={{ background: "none", border: "none", color: T.tx3, cursor: canDeleteTask ? "pointer" : "not-allowed", padding: 2, display: "flex", alignItems: "center", opacity: canDeleteTask ? 1 : 0.3 }}
                      >
                        <Icon name="x" size={12} color={T.tx3} />
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
        {/* Pagination */}
        {filtered.length > 0 && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 0", flexShrink: 0, borderTop: "1px solid rgba(217,219,221,0.06)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <span style={{ fontSize: 11, color: T.tx3 }}>{((currentPage - 1) * pageSize) + 1}–{Math.min(currentPage * pageSize, filtered.length)} of {filtered.length}</span>
              <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
                {[20, 50, 100].map(size => (
                  <button key={size} onClick={() => { setPageSize(size); setCurrentPage(1); }} style={{ padding: "2px 6px", borderRadius: 4, fontSize: 10, border: "none", background: pageSize === size ? "rgba(64,89,173,0.15)" : "transparent", color: pageSize === size ? "#FAFAFA" : T.tx3, cursor: "pointer", fontFamily: "inherit" }}>{size}</button>
                ))}
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
              <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} style={{ padding: "4px 8px", borderRadius: 4, border: "none", background: "transparent", color: currentPage === 1 ? "rgba(250,250,250,0.15)" : "#FAFAFA", cursor: currentPage === 1 ? "default" : "pointer", fontFamily: "inherit", fontSize: 12 }}>‹</button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => { let pn; if (totalPages <= 5) pn = i + 1; else if (currentPage <= 3) pn = i + 1; else if (currentPage >= totalPages - 2) pn = totalPages - 4 + i; else pn = currentPage - 2 + i; return (
                <button key={pn} onClick={() => setCurrentPage(pn)} style={{ width: 28, height: 28, borderRadius: 6, fontSize: 10, border: "none", fontFamily: "inherit", background: currentPage === pn ? "rgba(64,89,173,0.15)" : "transparent", color: currentPage === pn ? "#FAFAFA" : T.tx3, cursor: "pointer" }}>{pn}</button>
              ); })}
              <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} style={{ padding: "4px 8px", borderRadius: 4, border: "none", background: "transparent", color: currentPage === totalPages ? "rgba(250,250,250,0.15)" : "#FAFAFA", cursor: currentPage === totalPages ? "default" : "pointer", fontFamily: "inherit", fontSize: 12 }}>›</button>
            </div>
          </div>
        )}
      </div>

      </div>

      <TaskModal
        open={showAdd || !!editId}
        mode={editId ? "edit" : "create"}
        task={editId ? tasks.find(t => t.id === editId) : null}
        onClose={() => { setShowAdd(false); setEditId(null); }}
        onSave={(data) => {
          if (editId) {
            store.updateTask(editId, {
              title: data.title,
              description: data.description,
              pri: data.pri,
              status: data.status,
              taskType: data.taskType,
              due: data.due,
              ct: data.ct || undefined,
              assignee: data.assignee,
            });
          } else {
            store.addTask({
              title: data.title,
              description: data.description,
              pri: data.pri,
              status: data.status,
              taskType: data.taskType,
              due: data.due,
              ct: data.ct || null,
              assignee: data.assignee, // UUID from m._dbId
              done: data.status === "done",
            });
          }
          setShowAdd(false);
          setEditId(null);
        }}
      />
    </div>
  );
}
